import { useCallback, useEffect, useState } from "react";
import { Inbox } from "lucide-react";
import { api } from "@/api/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CopyButton } from "@/components/ui/copy-button";
import { CursorPager, type Cursors } from "@/components/ui/cursor-pager";
import { Spinner } from "@/components/ui/spinner";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { components } from "@dolfin/api-admin";

type InboundEmail = components["schemas"]["InboundEmailResponse"];
type Attachment = components["schemas"]["InboundEmailAttachmentResponse"];

const PAGE_SIZE = 20;

const MAX_ENVELOPE_CHARS = 20;

/** Truncated to one line so long values don't push the attachments column off the table; full value on hover. */
function EnvelopeValue({ value, className }: { value: string; className?: string }) {
  const truncated = value.length > MAX_ENVELOPE_CHARS;
  const shown = truncated ? `${value.slice(0, MAX_ENVELOPE_CHARS)}\u2026` : value;

  return (
    <span className="inline-flex items-start gap-1">
      {truncated ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className={className}>{shown}</span>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs break-all">{value}</TooltipContent>
        </Tooltip>
      ) : (
        <span className={className}>{shown}</span>
      )}
      <CopyButton value={value} className="shrink-0 [&_svg]:size-3" />
    </span>
  );
}

function skipReasonCopy(reason: Attachment["skipReason"]): string {
  switch (reason) {
    case "InlineImage":
      return "part of the email, not an attachment";
    case "UnsupportedFileType":
      return "file type not supported";
    case "FileTooLarge":
      return "too large";
    case "InvalidContent":
      return "could not be read";
    case "UploadFailed":
      return "failed on our side, please re-send";
    default:
      return "not taken";
  }
}

function rejectionCopy(reason: InboundEmail["rejectionReason"]): string {
  switch (reason) {
    case "SenderNotAllowed":
      return "sender not allowed";
    case "AuthenticationFailed":
      return "failed email authentication";
    case "TokenNotResolved":
      return "unknown forwarding address";
    case "TenantNotFound":
      return "forwarding address no longer exists";
    default:
      return "refused";
  }
}

function StatusBadge({ email }: { email: InboundEmail }) {
  const ingested = Number(email.ingestedCount);
  const total = Number(email.attachmentCount);

  if (email.status === "Ingested")
    return <Badge variant="secondary">{ingested === 1 ? "1 bill" : `${ingested} bills`}</Badge>;
  if (email.status === "PartiallyIngested")
    return (
      <Badge variant="outline">
        {ingested} of {total} taken
      </Badge>
    );
  if (email.status === "Rejected")
    return <Badge variant="destructive">{rejectionCopy(email.rejectionReason)}</Badge>;
  if (email.status === "NoBillAttachments") return <Badge variant="destructive">Nothing taken</Badge>;
  if (email.status === "Failed") return <Badge variant="destructive">Failed</Badge>;
  return <Badge variant="outline">Processing</Badge>;
}

/** The bill an attachment became; it keeps its id when routed to a site, so one id covers both. */
function CreatedBillId({ attachment }: { attachment: Attachment }) {
  const id = attachment.billId ?? attachment.companyInboxBillId;
  if (!id) return null;

  return (
    <span className="inline-flex items-center gap-1">
      <code className="font-mono text-[11px] text-muted-foreground">{id}</code>
      <CopyButton value={id} className="[&_svg]:size-3" />
    </span>
  );
}

function AttachmentsCell({ email }: { email: InboundEmail }) {
  if (email.attachments.length === 0)
    return <span className="text-xs text-muted-foreground">No attachments</span>;

  return (
    <ul className="space-y-1">
      {email.attachments.map((a) => (
        <li key={a.id} className="flex flex-wrap items-center gap-2 text-xs">
          <span className={a.ingested ? "text-foreground" : "text-muted-foreground"}>
            {a.fileName}
          </span>
          {a.ingested ? (
            <>
              <Badge variant="secondary" className="px-1.5 py-0 text-[10px]">
                bill created
              </Badge>
              <CreatedBillId attachment={a} />
            </>
          ) : (
            <span className="text-muted-foreground">
              {skipReasonCopy(a.skipReason)}
              {a.skipReason === "UnsupportedFileType" && a.declaredContentType
                ? ` (${a.declaredContentType})`
                : ""}
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}

/** Every email that reached this company's forwarding address, and what became of it. */
export function CompanyInboundEmailsSection({ companyId }: { companyId: string }) {
  const [emails, setEmails] = useState<InboundEmail[] | null>(null);
  const [cursors, setCursors] = useState<Cursors | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ingested, setIngested] = useState<boolean | undefined>(undefined);
  const [cursor, setCursor] = useState<string | null>(null);

  const load = useCallback(async () => {
    const query: Record<string, string | number | boolean> = { Limit: PAGE_SIZE };
    if (ingested !== undefined) query.Ingested = ingested;
    if (cursor) query.Cursor = cursor;

    const { data, error: apiError } = await api.GET("/v1/companies/{companyId}/inbound-emails", {
      params: { path: { companyId }, query },
    });

    if (apiError || !data) {
      setError("Couldn't load the inbound emails for this company.");
      return;
    }
    setEmails(data.data ?? []);
    setCursors(data.meta?.cursors ?? null);
    setError(null);
  }, [companyId, ingested, cursor]);

  useEffect(() => {
    void load();
  }, [load]);

  function filter(next: boolean | undefined) {
    setIngested(next);
    setCursor(null);
    setEmails(null);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Inbox className="size-4" /> Inbound emails
        </CardTitle>
        <CardDescription>
          Everything sent to this company's forwarding address, newest first, whether or not it became
          a bill. If a supplier's invoice never appeared, it is listed here with the reason we could
          not take it. Only filenames and types are kept, never the attachment itself.
        </CardDescription>
      </CardHeader>

      <CardContent className="flex flex-col gap-4">
        <div className="flex items-center gap-2">
          <Button
            variant={ingested === undefined ? "default" : "outline"}
            size="sm"
            onClick={() => filter(undefined)}
          >
            All
          </Button>
          <Button
            variant={ingested === true ? "default" : "outline"}
            size="sm"
            onClick={() => filter(true)}
          >
            Became bills
          </Button>
          <Button
            variant={ingested === false ? "default" : "outline"}
            size="sm"
            onClick={() => filter(false)}
          >
            Nothing taken
          </Button>
        </div>

        {error ? (
          <p className="py-6 text-center text-sm text-destructive">{error}</p>
        ) : emails === null ? (
          <div className="flex items-center justify-center py-6">
            <Spinner className="size-5" />
          </div>
        ) : emails.length === 0 ? (
          <p className="rounded-md border border-dashed px-4 py-6 text-center text-sm text-muted-foreground">
            {ingested === false
              ? "Nothing was turned away. Every email sent to this company produced a bill."
              : "Nothing has been forwarded to this company yet."}
          </p>
        ) : (
          <div className="overflow-hidden rounded-md border border-border">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/40">
                  <TableHead>Attachments</TableHead>
                  <TableHead>From</TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead>Received</TableHead>
                  <TableHead className="text-right">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {emails.map((email) => (
                  <TableRow key={email.id}>
                    <TableCell className="align-top">
                      <AttachmentsCell email={email} />
                    </TableCell>
                    <TableCell className="align-top">
                      <EnvelopeValue value={email.fromAddress} className="font-mono text-xs" />
                    </TableCell>
                    <TableCell className="align-top text-xs">
                      {email.subject ? (
                        <EnvelopeValue value={email.subject} />
                      ) : (
                        <span className="text-muted-foreground">(no subject)</span>
                      )}
                    </TableCell>
                    <TableCell className="align-top text-xs whitespace-nowrap text-muted-foreground">
                      {new Date(email.receivedAt).toLocaleString(undefined, {
                        dateStyle: "medium",
                        timeStyle: "short",
                      })}
                    </TableCell>
                    <TableCell className="align-top text-right">
                      <StatusBadge email={email} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        <CursorPager
          cursors={cursors}
          onNavigate={(next) => {
            setEmails(null);
            setCursor(next);
          }}
        />
      </CardContent>
    </Card>
  );
}
